/**
 * @fileoverview ClubOS Training Sessions page — weekly calendar and session list
 */

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

// Mock data
import { sessions } from "data/mockData";

// TODO: Replace with API call when backend is ready
// import { coachingService } from "api";

const INTENSITY_COLOR = { High: "error", Medium: "warning", Low: "success" };
const TYPE_ICON = { Tactical: "chess", Technical: "sports_soccer", Physical: "fitness_center", Recovery: "self_improvement", "Match Prep": "stadium", "Match Day": "emoji_events", "Rest Day": "hotel" };
const TYPE_COLOR = { Tactical: "info", Technical: "primary", Physical: "error", Recovery: "success", "Match Prep": "warning", "Match Day": "dark", "Rest Day": "secondary" };

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function Training() {
  // TODO: Uncomment when API is ready
  // const [data, setData] = useState(sessions);
  // useEffect(() => { coachingService.getSessions().then(setData).catch(console.error); }, []);

  const activeSessions = sessions.filter((s) => s.type !== "Rest Day" && s.type !== "Match Day");
  const totalMinutes = activeSessions.reduce((acc, s) => acc + s.duration, 0);
  const avgIntensity = activeSessions.filter((s) => s.intensity === "High").length;
  const thisWeekSessions = activeSessions.length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <SoftTypography variant="h4" fontWeight="bold" color="dark">
            Training Sessions
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            Week commencing 16 March 2026 · FC Metropolitan
          </SoftTypography>
        </SoftBox>

        {/* Quick stats */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <MiniStatisticsCard
                title={{ text: "Sessions This Week" }}
                count={String(thisWeekSessions)}
                percentage={{ color: "info", text: "planned" }}
                icon={{ color: "info", component: "event" }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MiniStatisticsCard
                title={{ text: "High Intensity Sessions" }}
                count={String(avgIntensity)}
                percentage={{ color: "error", text: "this week" }}
                icon={{ color: "error", component: "fitness_center" }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MiniStatisticsCard
                title={{ text: "Total Training Minutes" }}
                count={String(totalMinutes)}
                percentage={{ color: "success", text: "this week" }}
                icon={{ color: "success", component: "timer" }}
              />
            </Grid>
          </Grid>
        </SoftBox>

        {/* Weekly calendar overview */}
        <SoftBox mb={3}>
          <Card>
            <SoftBox p={3}>
              <SoftTypography variant="h6" fontWeight="medium" mb={2}>
                Weekly Schedule — w/c 16 March 2026
              </SoftTypography>
              <Grid container spacing={1}>
                {DAYS.map((day, di) => {
                  const session = sessions[di];
                  return (
                    <Grid key={day} item xs={12 / 7} sx={{ minWidth: 80 }}>
                      <SoftBox
                        textAlign="center"
                        p={1}
                        borderRadius="lg"
                        bgcolor={session?.type === "Rest Day" ? "#f8f9fa" : session?.type === "Match Day" ? "#fff3e0" : "#e8f5e9"}
                        border={`1px solid ${session?.type === "Match Day" ? "#ffb74d" : "#c8e6c9"}`}
                        sx={{ minHeight: 80 }}
                      >
                        <SoftTypography variant="caption" fontWeight="bold" display="block" color="text">
                          {day}
                        </SoftTypography>
                        {session && (
                          <>
                            <SoftTypography variant="caption" display="block" sx={{ fontSize: "0.65rem", mt: 0.5 }}>
                              {session.time}
                            </SoftTypography>
                            <SoftBadge
                              badgeContent={session.type}
                              color={TYPE_COLOR[session.type] || "info"}
                              size="xs"
                              container
                              sx={{ mt: 0.5 }}
                            />
                          </>
                        )}
                      </SoftBox>
                    </Grid>
                  );
                })}
              </Grid>
            </SoftBox>
          </Card>
        </SoftBox>

        {/* Session list */}
        <Card>
          <SoftBox p={3}>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <SoftTypography variant="h6" fontWeight="medium">
                Session Details
              </SoftTypography>
              <SoftButton variant="gradient" color="info" size="small">
                Add Session
              </SoftButton>
            </SoftBox>

            {sessions.map((session) => (
              <SoftBox
                key={session.id}
                display="flex"
                alignItems="center"
                p={1.5}
                mb={1}
                borderRadius="lg"
                sx={{
                  backgroundColor: session.type === "Rest Day" ? "#f8f9fa" : "white",
                  border: "1px solid #e9ecef",
                  "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                }}
              >
                {/* Date */}
                <SoftBox width={90} flexShrink={0}>
                  <SoftTypography variant="caption" fontWeight="bold" display="block" color="dark">
                    {new Date(session.date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text">{session.time}</SoftTypography>
                </SoftBox>

                {/* Type icon */}
                <SoftBox
                  bgColor={TYPE_COLOR[session.type] || "info"}
                  variant="gradient"
                  width="2.2rem"
                  height="2.2rem"
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mr={2}
                  flexShrink={0}
                >
                  <Icon sx={{ color: "white", fontSize: "1rem" }}>
                    {TYPE_ICON[session.type] || "sports_soccer"}
                  </Icon>
                </SoftBox>

                {/* Details */}
                <SoftBox flex={1}>
                  <SoftTypography variant="button" fontWeight="medium">{session.type}</SoftTypography>
                  {session.notes && (
                    <SoftTypography variant="caption" color="text" display="block">
                      {session.notes}
                    </SoftTypography>
                  )}
                </SoftBox>

                {/* Badges */}
                <SoftBox display="flex" gap={1} flexShrink={0} flexWrap="wrap" justifyContent="flex-end">
                  {session.intensity !== "Low" || session.type !== "Rest Day" ? (
                    <SoftBadge
                      badgeContent={session.intensity}
                      color={INTENSITY_COLOR[session.intensity] || "info"}
                      size="xs"
                      container
                    />
                  ) : null}
                  {session.duration > 0 && (
                    <SoftBadge badgeContent={`${session.duration} min`} color="secondary" size="xs" container />
                  )}
                  {session.players > 0 && (
                    <SoftBadge badgeContent={`${session.players} players`} color="dark" size="xs" container />
                  )}
                </SoftBox>
              </SoftBox>
            ))}
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer company={{ href: "#", name: "FC Metropolitan" }} links={[]} />
    </DashboardLayout>
  );
}

export default Training;
